import React, { useEffect, useRef, useState } from 'react';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { SearchPanel } from './components/SearchPanel';
import { useSelector } from './store/useSelector';
import { useAction } from './store/useAction';
import { SearchModuleActionType } from './store/reducers';
import { groupBy } from './utils/groupBy';
import { Section } from './components/Section';
import { SectionItem } from './components/SectionItem';
import { MatchSection } from './components/sections/MatchSection';
import { HelpAnswerSection } from './components/sections/HelpAnswerSection';
import { EventSection } from './components/sections/EventSection';
import { LeagueSection } from './components/sections/LeagueSection';
import { TournamentSection } from './components/sections/TournamentSection';
import { SelectionSection } from './components/sections/SelectionSection';
import { CasinoSection } from './components/sections/CasinoSection';
import { PromotionSection } from './components/sections/PromotionSection';
import { RaceSection } from './components/sections/RaceSection';
import { SportSection } from './components/sections/SportSection';
import { NoResultSection } from './components/NoResultSection';
import { useSearchModule } from './hooks/useSearchModule';
import { useTranslations } from './hooks/useTranslations';
import { findSportId, getAllSportsInEntries } from './utils/filterBySports';
import { DEFAULT_FILTER_TAB } from './config';
const ItemComponents = {
    casinogame: CasinoSection,
    event: EventSection,
    helpanswer: HelpAnswerSection,
    helparticle: () => null,
    league: LeagueSection,
    market: () => null,
    match: MatchSection,
    promotion: PromotionSection,
    race: RaceSection,
    selection: SelectionSection,
    sport: SportSection,
    tournament: TournamentSection,
    xsell: () => null,
    static: () => null,
};
export function SearchModuleUi({ onRemovePreviousQuery }) {
    const { t } = useTranslations();
    const value = useSelector((state) => state.value);
    const open = useSelector((state) => state.open);
    const loading = useSelector((state) => state.loading);
    const searched = useSelector((state) => state.searched);
    const entries = useSelector((state) => state.entries);
    const entriesTimestamp = useSelector((state) => state.entriesTimestamp);
    const previousQueries = useSelector((state) => state.previousQueries);
    const popularQueries = useSelector((state) => state.popularQueries);
    const sports = useSelector((state) => state.sports);
    const [groups, setGroups] = useState([]);
    const [filterSportId, setFilterSportId] = useState(DEFAULT_FILTER_TAB.id);
    const setValue = useAction(SearchModuleActionType.changeValue);
    const setOpen = useAction(SearchModuleActionType.changeOpen);
    const currentlyUsedFilter = useRef(null);
    const [sportsInEntries, setSportsInEntries] = useState([
        DEFAULT_FILTER_TAB,
        ...getAllSportsInEntries(sports, entries),
    ]);
    const searchModule = useSearchModule();
    const handleEntriesViewUpdate = () => {
        const filteredEntries = entries.filter((entry) => {
            return filterSportId === DEFAULT_FILTER_TAB.id || findSportId(entry) === filterSportId;
        });
        if (filteredEntries.length === 0) {
            setFilterSportId(DEFAULT_FILTER_TAB.id);
            return;
        }
        const map = groupBy(filteredEntries, (x) => x.type);
        setGroups(Object.keys(map).map((type) => ({ type, items: map[type] })));
    };
    useEffect(() => {
        var _a;
        setSportsInEntries([DEFAULT_FILTER_TAB, ...getAllSportsInEntries(sports, entries)]);
        handleEntriesViewUpdate();
        (_a = currentlyUsedFilter === null || currentlyUsedFilter === void 0 ? void 0 : currentlyUsedFilter.current) === null || _a === void 0 ? void 0 : _a.scrollIntoView();
    }, [entries]);
    useEffect(() => {
        var _a;
        (_a = document.querySelector('.WhSearch-SearchPanel__overflowContainer')) === null || _a === void 0 ? void 0 : _a.scrollTo(0, 0);
    }, [entriesTimestamp]);
    useEffect(handleEntriesViewUpdate, [filterSportId]);
    useEffect(() => {
        setFilterSportId(DEFAULT_FILTER_TAB.id);
    }, [open]);
    const change = (query) => {
        setValue(query);
        searchModule.emit('source', { source: 'Search Input' });
    };
    const focus = () => setOpen(true);
    const createQueryCall = (query, source) => () => {
        setValue(query);
        searchModule.emit('source', { source });
    };
    const close = () => {
        setOpen(false);
        setValue('');
    };
    const baseProps = {
        placeholder: t('inputPlaceholder'),
        value: open ? value : '',
        open,
        loading: open && loading,
        onChange: change,
        onFocus: focus,
        onClose: close,
    };
    if (!open) {
        return React.createElement(SearchPanel, Object.assign({}, baseProps));
    }
    if (entries.length === 0) {
        const previousSearchesElement = previousQueries.length > 0 ? (React.createElement(Section, { title: t('sectionPreviousSearchesTitle') }, previousQueries.map((query) => (React.createElement(SectionItem, { key: query, expectedTitle: 0, expectedDescription: 1, action: onRemovePreviousQuery ? faTimes : undefined, onAction: () => onRemovePreviousQuery === null || onRemovePreviousQuery === void 0 ? void 0 : onRemovePreviousQuery(query), onClick: createQueryCall(query, 'Previous Search') }, query))))) : null;
        const popularSearchesElement = popularQueries.length > 0 ? (React.createElement(Section, { title: t('sectionPopularSearchesTitle') }, popularQueries.map((query) => query.query ? (React.createElement(SectionItem, { key: query.label, expectedTitle: 0, expectedDescription: 1, onClick: createQueryCall(query.query, 'Popular Search') }, query.label)) : (React.createElement(SectionItem, { key: query.label, expectedTitle: 0, expectedDescription: 1, onClick: createQueryCall(query.label, 'Popular Search') }, query.label))))) : null;
        const noResultsElement = searched ? React.createElement(NoResultSection, null) : null;
        return (React.createElement(SearchPanel, Object.assign({}, baseProps),
            noResultsElement,
            previousSearchesElement,
            popularSearchesElement));
    }
    return (React.createElement(SearchPanel, Object.assign({}, baseProps, { sportsInEntries: sportsInEntries, currentSportFilter: filterSportId, setUsedSport: setFilterSportId, selectedFilterReference: currentlyUsedFilter }), groups.map(({ type, items }) => {
        const Component = ItemComponents[type];
        return (
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        React.createElement(Component, { key: `${type}${entriesTimestamp}`, items: items, count: items.length }));
    })));
}
//# sourceMappingURL=SearchModuleUi.js.map